import { Contact } from "../../../views/crm-pages/contacts/types";
import { Mail, Phone, Clock, MessageSquare, PhoneCall, Video, Briefcase } from 'lucide-react';

export const ContactCard: React.FC<{
  contact: Contact;
  onClick: () => void;
  onInboxClick: (e: React.MouseEvent) => void;
  onActivityClick: (e: React.MouseEvent) => void;
}> = ({ contact, onClick, onInboxClick, onActivityClick }) => {
  const getTouchpointIcon = (type: string) => {
    switch (type) {
      case 'WhatsApp':
        return <MessageSquare className="w-3 h-3" />;
      case 'AI Call':
      case 'Call':
        return <PhoneCall className="w-3 h-3" />;
      case 'Web Chat':
        return <MessageSquare className="w-3 h-3" />;
      case 'Email':
        return <Mail className="w-3 h-3" />;
      default:
        return null;
    }
  };

  const getTouchpointColor = (type: string) => {
    switch (type) {
      case 'WhatsApp':
        return 'bg-green-100 text-green-700';
      case 'AI Call':
        return 'bg-purple-100 text-purple-700';
      case 'Call':
        return 'bg-orange-100 text-orange-700';
      case 'Web Chat':
        return 'bg-blue-100 text-blue-700';
      case 'Email':
        return 'bg-pink-100 text-pink-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div
      onClick={onClick}
      className="border border-gray-200 rounded-lg p-4 md:p-6 hover:shadow transition-shadow cursor-pointer mb-4"
    >
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex items-start gap-3 md:gap-4 flex-1 min-w-0">
          <div className={`${contact.avatarColor} rounded-full w-12 h-12 md:w-14 md:h-14 flex items-center justify-center text-white font-semibold text-lg flex-shrink-0`}>
            {contact.initials}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="text-lg md:text-xl font-semibold text-gray-900">{contact.name}</h3>
              <span
                className={`text-xs px-2 py-1 rounded ${
                  contact.status === 'Prospect'
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-green-100 text-green-700'
                }`}
              >
                {contact.status}
              </span>
            </div>

            <div className="flex items-center gap-2 text-gray-600 text-sm mb-2">
              <Briefcase className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                {contact.title} at {contact.company}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-600 mb-3">
              <div className="flex items-center gap-1">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{contact.email}</span>
              </div>
              <div className="flex items-center gap-1">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <span>{contact.phone}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>Added {contact.dateAdded}</span>
              </div>
            </div>

            <div className="flex items-center gap-2 mb-3">
              <span
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${getTouchpointColor(
                  contact.lastTouchpoint.type
                )}`}
              >
                {getTouchpointIcon(contact.lastTouchpoint.type)}
                {contact.lastTouchpoint.type}
              </span>
              <span className="text-sm text-gray-600">
                Last touchpoint: {contact.lastTouchpoint.message} • {contact.lastTouchpoint.time}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              {contact.tags.map((tag, index) => (
                <span
                  key={index}
                  className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-700"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="flex lg:flex-col gap-2 ">
          <button
            onClick={onInboxClick}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium flex-1 lg:flex-none justify-center"
          >
            <Mail className="w-4 h-4" />
            <span className="hidden sm:inline">Open Inbox</span>
          </button>
          <button
            onClick={onActivityClick}
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium flex-1 lg:flex-none justify-center"
          >
            <Video className="w-4 h-4" />
            <span className="hidden sm:inline">View Activity</span>
          </button>
        </div>
      </div>
    </div>
  );
};